// Viewer UI strings (English + Japanese).
//
// 構成:
// - STRINGS は locale -> key -> 文字列。型は en の辞書から導出し、ja の漏れを
//   コンパイル時に検出する (Record<StringKey, string> を ja にも要求)。
// - currentLocale はモジュール状態。t() / applyHtmlI18n() は呼び出し時に
//   currentLocale を読むので、setLocale 後の呼び出しは自動で新ロケールになる。
// - setLocale で localStorage 永続化 + applyHtmlI18n 再走 + listeners 通知。
//   listener は main.ts 側から layout picker 再構築・meta タグ更新などの
//   動的 UI 反映用に登録する。

export type Locale = "en" | "ja";
const LOCALE_STORAGE_KEY = "WinCast-viewer-locale";

const STRINGS_EN = {
  // Form / join 画面
  "join.pincodeLabel": "Pincode (9 digits)",
  "join.button": "Connect",
  "join.connecting": "Connecting to signaling server...",
  "join.waitingHost": "Waiting for host response...",
  "join.serverError": "Server error: ",
  "join.pinLengthError": "Pincode must be {n} digits",
  "join.disconnected": "Disconnected",
  "join.connectFailed": "Could not connect: ",
  "join.getExtension": "Get the sharing tool",

  // Stage / overlay
  "stage.connecting": "Connecting",
  "stage.connected": "Connected",
  "stage.disconnectedReason": "Disconnected ({reason})",
  "stage.connectFailed": "Connection failed",
  "stage.tempDisconnect": "Temporary disconnect",
  "stage.closed": "Closed",

  // Toolbar
  "toolbar.layoutLabel": "Layout",
  "toolbar.muteToggle": "Toggle mute",
  "toolbar.muteOn": "Sound ON",
  "toolbar.muteOff": "Sound OFF",
  "toolbar.fullscreen": "Fullscreen",
  "toolbar.leave": "Leave",
  "toolbar.zoomHint": "Scroll to zoom",
  "toolbar.annotate": "Annotate",

  // Annotation tools
  "annotate.off": "Off",
  "annotate.pen": "Pen",
  "annotate.rect": "Rectangle",
  "annotate.circle": "Circle",
  "annotate.red": "Red",
  "annotate.blue": "Blue",
  "annotate.yellow": "Yellow",
  "annotate.clear": "Clear",
  "annotate.undo": "Undo",
  "annotate.undoTitle": "Undo (Cmd/Ctrl+Z)",

  // Layouts
  "layout.fullscreen": "Fullscreen",
  "layout.free": "Free layout",
  "layout.room": "3D",
  "layout.horizontal": "Side-by-side",
  "layout.vertical": "Top/bottom",
  "layout.pip": "Picture-in-picture",
  "layout.mainRight": "1 main + 2 (right)",
  "layout.mainBottom": "1 main + 2 (bottom)",
  "layout.columns": "3 columns",
  "layout.rows": "3 rows",

  // Windows fallback
  "window.label": "Window {n}",

  // Session warning
  "session.endingIn": "Session ends in {mm}:{ss}",

  // ===== Meta / About page =====
  "meta.title": "WinCast - Screen & Audio Sharing",
  "meta.description":
    "WinCast shares up to 3 windows plus system audio with a single pincode. Viewers join in a browser. No installs, no accounts.",

  "hero.scrollHint": "What is WinCast?",

  "about.whatItDoes.eyebrow": "What it does",
  "about.whatItDoes.title":
    "One pincode. Stream your screen and system sound to anyone's browser.",
  "about.whatItDoes.lead":
    "The host picks up to three windows from the Chrome extension and shares a 9-digit pincode. The viewer opens this page in any browser and enters the code - no installs, no accounts. Screens and system sound flow directly between browsers.",
  "about.replay": "Replay",

  "about.howItWorks.eyebrow": "How it works",
  "about.howItWorks.title": "3 steps.",
  "about.howItWorks.lead":
    "A one-time setup for the host. Viewers just need the link and the code - they can join from any device, anytime.",
  "about.step1.title": "Install the Chrome extension",
  "about.step1.body":
    "Only the person sharing installs the WinCast Chrome extension. Viewers don't need to install anything.",
  "about.step2.title": "Pick windows and get a pincode",
  "about.step2.body":
    "Choose up to three windows from the extension popup, and a 9-digit pincode is generated. System sound can be included optionally.",
  "about.step3.title": "Viewers enter the pincode in a browser",
  "about.step3.body":
    "Just type the pincode on this page. The two browsers connect directly, and you can pick your preferred layout.",

  "about.why.eyebrow": "Why WinCast",
  "about.why.title": "Simple. Direct. Leaves no trace.",
  "about.feature1.title": "Share up to 3 windows at once",
  "about.feature1.body":
    "Each window is sent as its own track - no canvas compositing, no quality loss.",
  "about.feature2.title": "System sound included",
  "about.feature2.body":
    "Whatever is playing on your PC - music, notifications, video audio - reaches the viewer. Microphone audio is never sent.",
  "about.feature3.title": "Direct browser-to-browser",
  "about.feature3.body":
    "Video and system sound travel directly between the host and viewer browsers. No relay server, nothing stored on our side.",
  "about.feature4.title": "Flexible layouts",
  "about.feature4.body":
    "Presets (side-by-side, stacked, main + sub) plus free placement and a 3D room view.",
  "about.feature5.title": "Pincodes expire in 3 minutes",
  "about.feature5.body":
    "Unused pincodes are discarded quickly. Once the viewer connects, the code is invalidated too.",
  "about.feature6.title": "1-hour session limit",
  "about.feature6.body":
    "Prevents sessions from being left running and avoids unintended long shares.",

  "about.cta.title": "Try it now",
  "about.cta.body":
    "To watch, just enter a pincode. To share, install the extension.",
  "about.cta.backToTop": "Back to the top",
  "about.footer": "WinCast - Screen & Audio Sharing",

  // ===== Hero animation captions (canvas 内に描画される) =====
  "anim.tagline": "Screen & Audio Sharing",
  "anim.systemAudio": "system audio",
  "anim.directStream": "direct stream",
  "anim.viewerPc": "Viewer - PC",
  "anim.viewerMobile": "Viewer - Mobile",
  "anim.noInstalls": "no installs. no accounts.",

  // ===== Language toggle =====
  // 「切替先の言語名」をボタンに出す方式 (EN 表示中は "日本語", JA 表示中は "English")。
  "lang.toggleLabel": "Switch language",
  "lang.toggleOther": "日本語",
} as const satisfies Record<string, string>;

export type StringKey = keyof typeof STRINGS_EN;

const STRINGS_JA: Record<StringKey, string> = {
  "join.pincodeLabel": "ピンコード (9桁)",
  "join.button": "接続",
  "join.connecting": "シグナリングサーバに接続中...",
  "join.waitingHost": "ホストの応答を待っています...",
  "join.serverError": "サーバエラー: ",
  "join.pinLengthError": "ピンコードは {n} 桁で入力してください",
  "join.disconnected": "切断されました",
  "join.connectFailed": "接続に失敗しました: ",
  "join.getExtension": "共有ツールを入手",

  "stage.connecting": "接続中",
  "stage.connected": "接続済み",
  "stage.disconnectedReason": "切断 ({reason})",
  "stage.connectFailed": "接続に失敗しました",
  "stage.tempDisconnect": "一時的に切断",
  "stage.closed": "終了",

  "toolbar.layoutLabel": "レイアウト",
  "toolbar.muteToggle": "ミュート切替",
  "toolbar.muteOn": "音声 ON",
  "toolbar.muteOff": "音声 OFF",
  "toolbar.fullscreen": "全画面",
  "toolbar.leave": "退出",
  "toolbar.zoomHint": "スクロールで拡大",
  "toolbar.annotate": "アノテーション",

  "annotate.off": "オフ",
  "annotate.pen": "ペン",
  "annotate.rect": "四角",
  "annotate.circle": "円",
  "annotate.red": "赤",
  "annotate.blue": "青",
  "annotate.yellow": "黄",
  "annotate.clear": "全消去",
  "annotate.undo": "元に戻す",
  "annotate.undoTitle": "元に戻す (Cmd/Ctrl+Z)",

  "layout.fullscreen": "全画面",
  "layout.free": "自由配置",
  "layout.room": "3D",
  "layout.horizontal": "横並び",
  "layout.vertical": "縦並び",
  "layout.pip": "ピクチャー・イン・ピクチャー",
  "layout.mainRight": "メイン+右に2つ",
  "layout.mainBottom": "メイン+下に2つ",
  "layout.columns": "3列",
  "layout.rows": "3段",

  "window.label": "ウィンドウ {n}",

  "session.endingIn": "セッション終了まで {mm}:{ss}",

  "meta.title": "WinCast - Screen & Audio Sharing",
  "meta.description":
    "WinCast はピンコード1つで最大3つのウィンドウとシステム音声を共有します。視聴側はブラウザでつなぐだけ。インストールもアカウントも不要です。",

  "hero.scrollHint": "WinCast とは？",

  "about.whatItDoes.eyebrow": "できること",
  "about.whatItDoes.title":
    "ピンコード1つで、画面とシステム音声を相手のブラウザへ届ける",
  "about.whatItDoes.lead":
    "共有する側は Chrome 拡張機能で最大 3 つのウィンドウを選び、9 桁のピンコードを発行します。視聴する側はこのページをブラウザで開いてコードを入力するだけ。インストールもアカウント登録も不要で、画面とシステム音声がブラウザ間で直接やり取りされます。",
  "about.replay": "リプレイ",

  "about.howItWorks.eyebrow": "使い方",
  "about.howItWorks.title": "3 ステップ",
  "about.howItWorks.lead":
    "共有する側は最初に拡張機能を入れるだけ。視聴する側はリンクとピンコードがあれば、どの端末からでもすぐに参加できます。",
  "about.step1.title": "Chrome 拡張機能をインストール",
  "about.step1.body":
    "WinCast の Chrome 拡張機能は共有する側だけにインストールします。視聴する側は何も入れる必要がありません。",
  "about.step2.title": "ウィンドウを選んでピンコードを発行",
  "about.step2.body":
    "拡張機能のポップアップから最大 3 つのウィンドウを選ぶと、9 桁のピンコードが発行されます。システム音声を一緒に流すこともできます。",
  "about.step3.title": "視聴側はブラウザでピンコードを入力",
  "about.step3.body":
    "このページにピンコードを入力するだけ。2 つのブラウザが直接つながり、レイアウトも好みのものを選べます。",

  "about.why.eyebrow": "WinCast の特長",
  "about.why.title": "シンプルかつ、直接接続により痕跡を残さない",
  "about.feature1.title": "最大 3 ウィンドウを同時共有",
  "about.feature1.body":
    "各ウィンドウを個別のトラックで送るので、合成による画質劣化はありません。",
  "about.feature2.title": "システム音声も一緒に",
  "about.feature2.body":
    "PC で鳴っている音 (音楽・通知音・動画の音声) を視聴側に届けます。マイクの音声は送られません。",
  "about.feature3.title": "ブラウザ同士で直接やり取り",
  "about.feature3.body":
    "映像と音声は共有側と視聴側のブラウザ間で直接流れます。中継サーバを経由せず、こちらに何も記録しません。",
  "about.feature4.title": "柔軟なレイアウト",
  "about.feature4.body":
    "横並び・縦並び・メイン+サブのプリセットに加え、自由配置や 3D ルーム表示にも対応。",
  "about.feature5.title": "ピンコードは 3 分で失効",
  "about.feature5.body":
    "未使用のピンコードは短時間で破棄されます。視聴側がつながった時点でも無効化されます。",
  "about.feature6.title": "セッションは最長 1 時間",
  "about.feature6.body":
    "つけっぱなしを防ぎ、意図せず長時間共有してしまうことを避けます。",

  "about.cta.title": "さっそく試してみる",
  "about.cta.body":
    "視聴するだけならピンコードを入力。共有する側は拡張機能をインストールしてください。",
  "about.cta.backToTop": "トップへ戻る",
  "about.footer": "WinCast - Screen & Audio Sharing",

  "anim.tagline": "画面・音声共有",
  "anim.systemAudio": "システム音声",
  "anim.directStream": "ブラウザ間で直接接続",
  "anim.viewerPc": "PCで視聴",
  "anim.viewerMobile": "スマホで視聴",
  "anim.noInstalls": "インストール不要・アカウント不要",

  "lang.toggleLabel": "言語を切り替える",
  "lang.toggleOther": "English",
};

const STRINGS: Record<Locale, Record<StringKey, string>> = {
  en: STRINGS_EN,
  ja: STRINGS_JA,
};

function detectInitialLocale(): Locale {
  try {
    const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (saved === "en" || saved === "ja") return saved;
  } catch {
    // localStorage が無効でも続行 (Safari Private Mode 等)
  }
  const nav = typeof navigator !== "undefined" ? navigator.language ?? "" : "";
  return /^ja\b/i.test(nav) ? "ja" : "en";
}

let currentLocale: Locale = detectInitialLocale();
const listeners: Array<(loc: Locale) => void> = [];

export function getLocale(): Locale {
  return currentLocale;
}

export function setLocale(loc: Locale): void {
  if (loc === currentLocale) return;
  currentLocale = loc;
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, loc);
  } catch {
    // 保存できなくても切替は実施
  }
  applyHtmlI18n();
  for (const cb of listeners) cb(loc);
}

// listener を登録。返り値の関数で解除できる。main.ts は layout picker 再構築・
// meta 更新・status text 再描画などに使う。
export function onLocaleChange(cb: (loc: Locale) => void): () => void {
  listeners.push(cb);
  return () => {
    const i = listeners.indexOf(cb);
    if (i >= 0) listeners.splice(i, 1);
  };
}

export function t(
  key: StringKey,
  vars: Record<string, string | number> = {},
): string {
  const tpl: string = STRINGS[currentLocale][key];
  return Object.entries(vars).reduce<string>(
    (acc, [k, v]) => acc.replaceAll(`{${k}}`, String(v)),
    tpl,
  );
}

// HTML の data-i18n 属性をスキャンして textContent / placeholder / title / aria-label を差し替える
export function applyHtmlI18n(root: ParentNode = document): void {
  const dict = STRINGS[currentLocale];
  root.querySelectorAll<HTMLElement>("[data-i18n]").forEach((el) => {
    const key = el.dataset.i18n as StringKey | undefined;
    if (key && key in dict) el.textContent = t(key);
  });
  root
    .querySelectorAll<HTMLElement>("[data-i18n-placeholder]")
    .forEach((el) => {
      const key = el.dataset.i18nPlaceholder as StringKey | undefined;
      if (key && key in dict && "placeholder" in el) {
        (el as HTMLInputElement).placeholder = t(key);
      }
    });
  root.querySelectorAll<HTMLElement>("[data-i18n-title]").forEach((el) => {
    const key = el.dataset.i18nTitle as StringKey | undefined;
    if (key && key in dict) el.title = t(key);
  });
  root.querySelectorAll<HTMLElement>("[data-i18n-aria]").forEach((el) => {
    const key = el.dataset.i18nAria as StringKey | undefined;
    if (key && key in dict) el.setAttribute("aria-label", t(key));
  });
  document.documentElement.lang = currentLocale;
}
